import type { TaxRegistrationResponse, WorkLocationResponse } from '../types';
import { applyRegistration } from './mockWorkLocationRules';

/**
 * Synthetic locations that exercise every Step 5 list state (Figma S01-S19, S30-S32): a Main location, branches with and without a
 * registration, an In Progress and a Not Required one, a Remote Hub, a Client Site, a Temporary location and an inactive branch. Used only by
 * the mock gateway's test hook (seedStep5DataForTests). Every name, address and account number is invented; the account numbers are fake.
 */

function registration(partial: Partial<TaxRegistrationResponse> & Pick<TaxRegistrationResponse, 'status'>): TaxRegistrationResponse {
  return {
    effectiveDate: null, nexusEstablishedDate: null, stateAccountNumberMasked: null, suiAccountNumberMasked: null, sdiPflAccountNumberMasked: null,
    localTaxRegistrationMasked: null, localTaxAccountNumberMasked: null, notes: null, recordedAtUtc: '2026-02-01T00:00:00Z', ...partial,
  };
}

const REGISTERED = registration({
  status: 'Registered', effectiveDate: '2025-03-01', nexusEstablishedDate: '2025-01-15', stateAccountNumberMasked: '****0481', suiAccountNumberMasked: '****7720',
});

function location(
  id: string, name: string, type: WorkLocationResponse['locationType'], line1: string, city: string, state: string, zip: string, code: string,
  status: WorkLocationResponse['status'], reg: TaxRegistrationResponse | null, isDefault = false,
): WorkLocationResponse {
  const result: WorkLocationResponse = {
    id, name, addressLine1: line1, addressLine2: null, city, state, zip, country: 'US', county: null, phone: null, locationType: type, locationCode: code,
    timeZone: null, status, isDefault, createdAtUtc: '2026-01-01T00:00:00Z', updatedAtUtc: null, duplicateNameWarning: null,
    activeForPayroll: status === 'Active', version: 0,
    mailingAddress: { sameAsPhysical: true, addressLine1: null, addressLine2: null, city: null, state: null, zip: null, country: null },
    taxRegistration: null, taxRegistrationStatus: 'NotRegistered', registrationComplete: false, registrationIssues: [],
  };
  applyRegistration(result, reg);
  return result;
}

export interface ShowcaseIds {
  main: string; palmdale: string; remote: string; austin: string; bahaha: string; client: string; temporary: string; santaClarita: string; pending: string;
}

export function buildStep5ShowcaseLocations(main: WorkLocationResponse | undefined): { locations: WorkLocationResponse[]; ids: ShowcaseIds } {
  const ids: ShowcaseIds = {
    main: main?.id ?? 'showcase-main',
    palmdale: 'showcase-palmdale',
    remote: 'showcase-remote',
    austin: 'showcase-austin',
    bahaha: 'showcase-bahaha',
    client: 'showcase-client',
    temporary: 'showcase-temporary',
    santaClarita: 'showcase-santa-clarita',
    pending: 'showcase-pending',
  };
  const mainLocation = location(
    ids.main, 'Woodland Hills Main Office', null, main?.addressLine1 ?? '21550 Oxnard St', main?.city ?? 'Woodland Hills', main?.state ?? 'CA', main?.zip ?? '91367',
    'LOC-001', 'Active', REGISTERED, true,
  );
  return {
    ids,
    locations: [
      mainLocation,
      location(ids.palmdale, 'Palmdale Branch', 'Branch', '1000 E Palmdale Blvd', 'Palmdale', 'CA', '93550', 'LOC-002', 'Active', null),
      location(ids.remote, 'Remote Hub - West Coast', 'RemoteHub', '21550 Oxnard St', 'Woodland Hills', 'CA', '91367', 'LOC-003', 'Active', REGISTERED),
      location(ids.austin, 'Austin Branch', 'Branch', '21550 Concord St', 'Woodland Hills', 'CA', '91367', 'LOC-004', 'Active', REGISTERED),
      location(ids.bahaha, 'Bahaha Branch', 'Branch', '21551 Concord St', 'Woodland Hills', 'CA', '91367', 'LOC-005', 'Active', REGISTERED),
      location(ids.client, 'Oakline Client Site', 'ClientSite', '77 Harbor Way', 'Long Beach', 'CA', '90802', 'LOC-006', 'Active', registration({ status: 'NotRequired', notes: 'Employees work remotely at the client.' })),
      location(ids.temporary, 'Summer Pop-up', 'Temporary', '12 Pier Ave', 'Santa Monica', 'CA', '90401', 'LOC-007', 'Active', registration({ status: 'NotRegistered' })),
      location(ids.pending, 'Reno Branch', 'Branch', '500 Virginia St', 'Reno', 'NV', '89501', 'LOC-008', 'Active', registration({ status: 'InProgress' })),
      location(ids.santaClarita, 'Sta. Clarita Office', 'Branch', '21550 Oxnard St', 'Woodland Hills', 'CA', '91367', 'LOC-009', 'Inactive', registration({ status: 'NotRequired', notes: 'Closed location.' })),
    ],
  };
}
